const RESERVED_KEYS = new Set(["profiles"]);

function labelFromKey(key) {
  return String(key || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase());
}

function normalizeEntry(entry, index) {
  return {
    id: String(entry?.id || `social-${index + 1}`),
    platform: String(entry?.platform || entry?.displayName || entry?.name || "").trim(),
    url: String(entry?.url || "").trim(),
    iconKey: String(entry?.iconKey || entry?.icon || "").trim(),
    order: Number.isFinite(Number(entry?.order)) ? Number(entry.order) : index
  };
}

export function normalizeSocialProfiles(value) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const legacy = Object.entries(source)
    .filter(([key, url]) => !RESERVED_KEYS.has(key) && typeof url === "string" && url.trim())
    .map(([key, url], index) => normalizeEntry({ id: `legacy-${key}`, platform: labelFromKey(key), url, iconKey: key, order: index }, index));
  const custom = (Array.isArray(source.profiles) ? source.profiles : []).map(normalizeEntry);
  const byPlatform = new Map();
  for (const profile of [...legacy, ...custom].sort((a, b) => a.order - b.order)) {
    if (!profile.platform || !profile.url) continue;
    byPlatform.set(profile.platform.toLowerCase(), profile);
  }
  return [...byPlatform.values()].sort((a, b) => a.order - b.order);
}

export function socialProfilesForEditor(value) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const profiles = normalizeSocialProfiles(source);
  const takeLegacy = name => profiles.find(profile => profile.platform.toLowerCase() === name)?.url || "";
  const legacyCustom = Object.entries(source)
    .filter(([key, url]) => !["profiles", "facebook", "twitter"].includes(key) && typeof url === "string" && url.trim())
    .map(([key, url], order) => normalizeEntry({ id: `legacy-${key}`, platform: labelFromKey(key), url, iconKey: key, order }, order));
  const explicitCustom = (Array.isArray(source.profiles) ? source.profiles : []).map(normalizeEntry);
  const custom = [...legacyCustom, ...explicitCustom].filter(profile => !["facebook", "twitter"].includes(profile.platform.toLowerCase()));
  return {
    facebook: takeLegacy("facebook"),
    twitter: takeLegacy("twitter"),
    profiles: custom.map((profile, order) => ({ ...profile, order }))
  };
}

function validateUrl(value, label) {
  if (!value) return "";
  let url;
  try { url = new URL(value); } catch { throw new Error(`${label} must be a complete, valid URL.`); }
  if (!["http:", "https:"].includes(url.protocol)) throw new Error(`${label} must use HTTP or HTTPS.`);
  return url.href;
}

export function validateSocialProfileSettings(value) {
  const editor = socialProfilesForEditor(value);
  const result = {
    facebook: validateUrl(editor.facebook, "Facebook URL"),
    twitter: validateUrl(editor.twitter, "Twitter URL"),
    profiles: []
  };
  for (let index = 0; index < editor.profiles.length; index += 1) {
    const profile = editor.profiles[index];
    if (!profile.platform && !profile.url) continue;
    if (!profile.platform) throw new Error(`Social profile ${index + 1} requires a platform name.`);
    if (!profile.url) throw new Error(`${profile.platform} requires a URL.`);
    result.profiles.push({
      id: profile.id,
      platform: profile.platform,
      url: validateUrl(profile.url, `${profile.platform} URL`),
      iconKey: profile.iconKey,
      order: result.profiles.length
    });
  }
  return result;
}
